import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { useRouter } from "next/router";

import { ACTIVITIES_SELECTION_RELEASE } from "@/assignments/constants/activitySelectionReleaseDate";
import { getIsSavedAssignment } from "@/assignments/utils/assignmentUrlHelpers";
import { formatAssignmentForReducer } from "@/assignments/utils/formatAssignmentForReducer";
import { getCopiedAssignmentInitialState } from "@/assignments/utils/getCopiedAssignmentInitialState";
import useAttachmentsContext from "@/view/utils/useAttachmentsContext";

const useSetAssignmentOnLoad = ({
  fetchAssignment,
  handleSetAssignment,
  isStudent,
  isSelectedAssignmentLoading,
  latestAssignment,
  selectedAssignment,
  setIsLoading,
}) => {
  const router = useRouter();
  const { selectedAssignmentId, copiedAssignmentId } = router.query;
  const isSavedAssignment = getIsSavedAssignment(selectedAssignmentId);
  const { setVisibleActivityIds } = useAttachmentsContext();
  const [fetchCopiedAssignment, setFetchCopiedAssignment] =
    useState(copiedAssignmentId);

  const setAssignmentReducerState = (assignment) => {
    const assignmentForReducer = formatAssignmentForReducer(
      assignment,
      isStudent,
    );
    handleSetAssignment(assignmentForReducer);
  };

  const setVisibleActivityState = (assignment) => {
    if (!assignment?.date_modified) return;

    const modifiedDate = dayjs(assignment.date_modified);
    const modifiedBeforeActivitySelectionFeature = modifiedDate.isBefore(
      dayjs(ACTIVITIES_SELECTION_RELEASE),
    );

    if (modifiedBeforeActivitySelectionFeature) return;

    const activityMemberIds = assignment?.activity_members?.map(
      (member) => member.content_id,
    );
    setVisibleActivityIds(activityMemberIds);
  };

  const handleStudentAssignmentSet = () => {
    if (!isSavedAssignment && latestAssignment) {
      setVisibleActivityState(latestAssignment);
      return setAssignmentReducerState(latestAssignment);
    }
    if (selectedAssignment) {
      setVisibleActivityState(selectedAssignment);
      return setAssignmentReducerState(selectedAssignment);
    }
  };

  const handleTeachersAssignmentSet = async () => {
    // selectedAssignmentId has been provided and loaded into selectedAssignment
    if (isSavedAssignment && selectedAssignment) {
      setVisibleActivityState(selectedAssignment);
      return setAssignmentReducerState(selectedAssignment);
    }

    // copiedAssignmentId has been provided, and has not yet been fetched/loaded into selectedAssignment
    if (copiedAssignmentId && !selectedAssignment && fetchCopiedAssignment) {
      const response = await fetchAssignment({
        id: copiedAssignmentId,
        queryParams: { include_template: true },
      });
      if (response?.data) {
        const assignment = getCopiedAssignmentInitialState(response.data);
        setVisibleActivityState(response?.data);
        return setAssignmentReducerState(assignment);
      }
      // Don't try to fetch copied assignment again
      setFetchCopiedAssignment(false);
    }
  };

  useEffect(() => {
    const setAssignmentOnLoad = () => {
      if (isStudent) {
        handleStudentAssignmentSet();
      } else {
        handleTeachersAssignmentSet();
      }

      if (setIsLoading) setIsLoading(false);
    };

    // Load assignment when we're done fetching assignment data from ViewPageContent.
    if (!isSelectedAssignmentLoading) {
      setAssignmentOnLoad();
    }
  }, [isSelectedAssignmentLoading]);
};

export default useSetAssignmentOnLoad;
